/**
 * DairyGoatAnatomy.jsx
 * MbuziPlus — Topic 2: Dairy Goat Anatomy & Health Detection
 *
 * Structure mirrors BreedSelection.jsx with these additions:
 *  • Every lesson has an annotated image section showing the exact body part
 *    or system being discussed, with labelled callouts.
 *  • Every lesson now uses its OWN dedicated image (no more sharing/reuse
 *    across sections) — see IMGS below. Swap the placeholder URLs for real
 *    photos whenever they're ready.
 *  • The image container no longer force-crops to a fixed height. It sizes
 *    itself to the image's natural aspect ratio, so any image — portrait,
 *    landscape, square, tall, wide — displays in full and fits correctly.
 *  • Health detection lessons include a visual "what to look for" panel.
 *  • All progress, quiz answers, and journal persist to Firestore under
 *    imfuyo_academy/{token}/mbuziplus_course/{token} → topic key "anatomy".
 *
 * Props:
 *   isDark  : boolean
 *   user    : { token: string }
 *   onBack  : () => void
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { optionOrder } from "./shuffle";
import {
  ChevronLeft, ChevronRight, BookOpen, Film, HelpCircle,
  PenLine, Check, X, Save, Loader2, CheckCircle2, Circle,
} from "lucide-react";

import { db } from "../../Libs/firebase-config.mjs";
import { ModuleIntro, SourcesPanel } from "./ModuleFrame.jsx";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ─────────────────────────────────────────────
   ANIMATIONS & CSS
   ───────────────────────────────────────────── */
const css = `
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }

  .da-fade  { animation: fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both; }
  .da-card  { transition: transform 0.15s ease, border-color 0.15s ease; }
  .da-card:hover { transform: translateY(-2px); }
  .da-option { transition: border-color 0.12s, background 0.12s, transform 0.08s; cursor:pointer; }
  .da-option:hover { transform: translateX(2px); }

  /* image container — sizes to the image itself, no forced crop */
  .da-img-wrap {
    position: relative;
    overflow: hidden;
    display: block;
    width: 100%;
    line-height: 0; /* removes inline-image baseline gap */
  }
  .da-img-wrap img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 70vh;      /* guards against extreme portrait images on small screens */
    object-fit: contain;   /* never crops — always shows the full image */
    background: #0000000a;
  }
`;

/* ─────────────────────────────────────────────
   IMAGE ASSETS
   Every lesson section gets its own dedicated image key.
   Replace these placeholder URLs with real photos whenever
   they're ready — nothing else in the component needs to change.
   ───────────────────────────────────────────── */
const IMGS = {
  // "overview" — The Dairy Goat at a Glance (full body reference)
  overview: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933952/IMG-20260702-WA0019_wkwdlp.jpg",

  // "digestive" — left-flank / rumen emphasis shot
  digestive: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933988/IMG-20260702-WA0018_yszhou.jpg",

  // "mammary" — udder close-up
  mammary: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783934046/IMG-20260702-WA0017_pwp3ed.jpg",

  // "musculoskeletal" — hooves / legs close-up
  musculoskeletal: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933860/IMG-20260708-WA0019_ujtgsg.jpg",

  // "integumentary" — head / eyes / nose close-up
  integumentary: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933918/IMG-20260708-WA0018_r2pl7a.jpg",

  // "body-condition" — side view showing loin / ribs / sternum
  bodyCondition: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933765/IMG-20260713-WA0000_isw7uq.jpg",

  // "lymph-nodes" — full body, lymph node sites emphasised
  lymphNodes: "https://res.cloudinary.com/dpymwa41m/image/upload/v1783933834/IMG-20260708-WA0020_gy5yx3.jpg",

  // Video lesson
  video: "https://res.cloudinary.com/dpymwa41m/video/upload/v1783937017/Dairy_Goat_Anatomy_p2pjzk.mp4",
  videoPoster: "https://placehold.co/1280x720/e8f5e8/2d5a34?text=Video%3A+Health+Walkthrough+poster",
};

/* ─────────────────────────────────────────────
   ANNOTATED IMAGE COMPONENT
   The source photos already have their callout
   numbers/markers baked in, so this just displays
   the image cleanly (no interactive hover dots)
   plus a numbered legend underneath matching what's
   labelled on the photo itself.
   The wrapper sizes itself to the image's natural
   aspect ratio (object-fit: contain, height: auto),
   so any image dimensions display correctly without
   cropping or forcing a fixed box.
   ───────────────────────────────────────────── */
function AnnotatedImage({ src, alt, annotations = [], isDark, caption }) {
  return (
    <div style={{ marginBottom: "28px" }}>
      <div
        className="da-img-wrap"
        style={{
          borderRadius: "14px",
          border: `1px solid ${isDark ? "#223328" : "#d8edd8"}`,
          background: isDark ? "#0f1f13" : "#eef7ee",
        }}
      >
        <img src={src} alt={alt} loading="lazy" />
      </div>

      {/* Legend — number + name of each labelled part shown in the photo */}
      {annotations.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: "8px 14px",
          marginTop: "10px", paddingLeft: "4px",
        }}>
          {annotations.map((ann, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "18px", height: "18px", borderRadius: "50%",
                background: "#3E7A32",
                color: "#fff", fontSize: "9px", fontWeight: 800,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: "12px", color: isDark ? "#7fa183" : "#5a7a5d", lineHeight: 1.4 }}>
                {ann.label}
              </span>
            </div>
          ))}
        </div>
      )}

      {caption && (
        <p style={{
          margin: "8px 0 0", fontSize: "12px", fontStyle: "italic",
          color: isDark ? "#4a6650" : "#8aaa8d", lineHeight: 1.5, paddingLeft: "4px",
        }}>
          {caption}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HEALTH SIGNAL PANEL
   Quick-reference "what to look for" cards
   shown at the end of health-related lessons.
   ───────────────────────────────────────────── */
function HealthSignalPanel({ signals, isDark }) {
  return (
    <div style={{
      marginTop: "24px",
      borderRadius: "14px",
      border: `1px solid ${isDark ? "#1e3321" : "#c3e6c3"}`,
      overflow: "hidden",
    }}>
      <div style={{
        padding: "10px 16px",
        background: isDark ? "#0f2218" : "#F1F5EE",
        borderBottom: `1px solid ${isDark ? "#1e3321" : "#D5E8CB"}`,
      }}>
        <p style={{ margin: 0, fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: isDark ? "#8DBB7E" : "#285020" }}>
          Quick Reference — What to Look For
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {signals.map((s, i) => (
          <div key={i} style={{
            display: "flex", gap: "14px", alignItems: "flex-start",
            padding: "12px 16px",
            borderBottom: i < signals.length - 1 ? `1px solid ${isDark ? "#182e1e" : "#e2efe2"}` : "none",
            background: isDark ? "#0d1a0f" : "#fff",
          }}>
            <div style={{
              flexShrink: 0, marginTop: "2px",
              width: "8px", height: "8px", borderRadius: "50%",
              background: s.flag === "green" ? "#3E7A32" : s.flag === "amber" ? "#f59e0b" : "#ef4444",
              boxShadow: `0 0 0 3px ${
                s.flag === "green"
                  ? (isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.12)")
                  : s.flag === "amber"
                    ? (isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.12)")
                    : (isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.12)")
              }`,
            }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: "0 0 3px", fontSize: "13px", fontWeight: 700, color: isDark ? "#e0f0e0" : "#1a2e1c" }}>
                {s.sign}
              </p>
              <p style={{ margin: 0, fontSize: "12.5px", color: isDark ? "#7fa183" : "#5a7a5d", lineHeight: 1.55 }}>
                {s.meaning}
              </p>
            </div>
            <span style={{
              flexShrink: 0, fontSize: "10px", fontWeight: 700,
              padding: "2px 8px", borderRadius: "50px",
              background: s.flag === "green"
                ? (isDark ? "rgba(34,197,94,0.12)" : "#E3EEDD")
                : s.flag === "amber"
                  ? (isDark ? "rgba(245,158,11,0.12)" : "#fef9c3")
                  : (isDark ? "rgba(239,68,68,0.12)" : "#fee2e2"),
              color: s.flag === "green"
                ? (isDark ? "#8DBB7E" : "#285020")
                : s.flag === "amber"
                  ? (isDark ? "#fcd34d" : "#b45309")
                  : (isDark ? "#fca5a5" : "#dc2626"),
              border: `1px solid ${
                s.flag === "green"
                  ? (isDark ? "#14301A" : "#B7D9A8")
                  : s.flag === "amber"
                    ? (isDark ? "#78350f" : "#fde68a")
                    : (isDark ? "#7f1d1d" : "#fecaca")
              }`,
            }}>
              {s.flag === "green" ? "Normal" : s.flag === "amber" ? "Monitor" : "Act now"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INSIGHT BLOCK — field-notebook style
   ───────────────────────────────────────────── */
function InsightBlock({ item, isDark, index = 0 }) {
  const ruleColor  = isDark ? "#14301A" : "#3E7A32";
  const dotColor   = isDark ? "#14301A" : "#3E7A32";
  const labelColor = isDark ? "#8DBB7E" : "#285020";
  const bodyColor  = isDark ? "#c8e0cc" : "#2d3e31";

  return (
    <div
      className="da-fade"
      style={{
        position: "relative",
        paddingLeft: "20px",
        paddingBottom: index === -1 ? 0 : "28px",
        borderLeft: `2px solid ${ruleColor}`,
        animationDelay: `${Math.max(0, index) * 0.05}s`,
      }}
    >
      <div style={{
        position: "absolute", left: "-5px", top: "5px",
        width: "8px", height: "8px", borderRadius: "50%",
        background: dotColor,
      }} />
      <p style={{
        margin: "0 0 5px", fontSize: "11px", fontWeight: 800,
        letterSpacing: "0.08em", textTransform: "uppercase",
        color: labelColor, lineHeight: 1.4,
      }}>
        {item.h}
      </p>
      <p style={{
        margin: 0, fontSize: "15px", lineHeight: 1.75, color: bodyColor,
        fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 400,
      }}>
        {item.p}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   CONTENT DATA
   ───────────────────────────────────────────── */
const LESSONS = {
  "overview": {
    title: "The Dairy Goat at a Glance",
    intro: "Before you can spot what is wrong, you need to know what is right. This lesson gives you the map and the first number to measure.",
    image: {
      src: IMGS.overview,
      alt: "Full body dairy goat showing key anatomical reference points",
      caption: "German Alpine dairy goat — the numbered points are the landmarks you will use throughout this topic.",
      annotations: [
        { x: 50, y: 20, label: "Spine / topline" },
        { x: 30, y: 45, label: "Shoulder / withers" },
        { x: 65, y: 55, label: "Flank (body condition)" },
        { x: 50, y: 80, label: "Udder / mammary gland" },
        { x: 20, y: 80, label: "Fore leg & hoof" },
        { x: 80, y: 80, label: "Hind leg & hoof" },
      ],
    },
    items: [
          {
                "h": "Why anatomy matters for a farmer",
                "p": "You do not need to know every bone. You need to know where to look, and what you are looking at, when something seems wrong. Anatomy is your map. Health checking is how you read it."
          },
          {
                "h": "Four areas that drive dairy production",
                "p": "This module covers four areas: the digestive system (the stomach compartments), the udder, the legs and hooves, and the skin, coat, eyes and nose. Each one shows signals when the goat is under stress."
          },
          {
                "h": "Your starting number: body condition",
                "p": "Before anything else, give each doe a body condition score. The scale runs from 1 (extremely thin) to 5 (very fat), and the healthy range for goats is 2.5 to 4.0 (eXtension). It costs nothing and takes a few seconds with your hands."
          },
          {
                "h": "Three questions to ask every day",
                "p": "Is she eating and chewing her cud? Does she walk evenly on all four legs? Are her udder and milk normal? A no to any of these is your signal to look closer and write it down."
          },
          {
                "h": "Check yourself",
                "p": "Question: What is the healthy body condition range for a goat? Answer: 2.5 to 4.0 on the 1 to 5 scale."
          }
    ],
    sources: ["eXtension Goat Community of Practice, Goat Body Condition Score How-To"],
  },

  "digestive": {
    title: "The Digestive System: From Rumen to Milk",
    intro: "Goats are ruminants. What happens in the stomach decides how much milk she gives and how healthy she stays.",
    image: {
      src: IMGS.digestive,
      alt: "Dairy goat showing the rumen and digestive anatomy location on the body",
      caption: "The rumen occupies the entire left side of the abdomen. A distended left flank is your first sign of bloat.",
      annotations: [
        { x: 60, y: 55, label: "Rumen (left flank)" },
        { x: 70, y: 62, label: "Reticulum (front of rumen)" },
        { x: 72, y: 50, label: "Omasum" },
        { x: 75, y: 45, label: "Abomasum (true stomach)" },
        { x: 80, y: 60, label: "Small intestine" },
      ],
    },
    items: [
          {
                "h": "The four-part stomach",
                "p": "A goat has four stomach compartments. The rumen is the fermentation vat where microbes break down fibre. The reticulum is called the hardware stomach because it traps swallowed objects, such as wire or nails, and helps move food along. The omasum removes water and absorbs nutrients. The abomasum is the true stomach, where acid digests food (Cornell University)."
          },
          {
                "h": "What the rumen does",
                "p": "Microbes in the rumen supply enzymes that break down fibre, build protein and make B vitamins. Fermentation makes heat and gas. The gas must come out as burps. When gas is trapped, the rumen swells and the goat bloats (Cornell University)."
          },
          {
                "h": "Chewing the cud",
                "p": "A goat chews feed, swallows it, and later brings a ball of it, the cud, back to the mouth to chew again. A goat that stops chewing her cud is telling you something is wrong with her rumen, her feed or her health."
          },
          {
                "h": "How a kid's stomach changes",
                "p": "At birth a kid's rumen, reticulum and omasum are tiny and do not work yet. Milk goes straight to the abomasum through a groove in the throat. As the kid eats roughage, the rumen grows and its microbes build up (Cornell University). Later modules on feeding and kid rearing cover timing in detail."
          },
          {
                "h": "Reading the left flank",
                "p": "The rumen sits on the goat's left side. A hollow left flank behind the ribs usually means she has not eaten enough. A tight, drum-like left flank means bloat, which is an emergency. A moderately full left flank after feeding is what you want to see."
          },
          {
                "h": "Check yourself",
                "p": "Question: Which stomach compartment is the true stomach with acid digestion? Answer: The abomasum."
          }
    ],
    healthSignals: [
          {
                "sign": "Full, rounded left flank and she is chewing her cud",
                "meaning": "Digestion is working and she has been eating.",
                "flag": "green"
          },
          {
                "sign": "Hollow left flank behind the ribs",
                "meaning": "She has not eaten enough or has been off feed. Check feed, water and body condition.",
                "flag": "amber"
          },
          {
                "sign": "Tight, drum-like left flank",
                "meaning": "Bloat. Trapped gas can kill quickly. Call your veterinarian at once.",
                "flag": "red"
          },
          {
                "sign": "Stops eating and stops chewing her cud",
                "meaning": "The rumen has slowed or stopped. This can come from stress, fever or illness. Call your veterinarian the same day.",
                "flag": "red"
          },
          {
                "sign": "Loose, watery dung or mucus strands",
                "meaning": "Gut irritation. Possible worms, a feed change, or coccidiosis in kids. Check her eyes for anaemia and tell your veterinarian.",
                "flag": "amber"
          }
    ],
    sources: ["Cornell University, New York 4-H Animal Science, Digestive System of Goats"],
  },

  "mammary": {
    title: "The Mammary Gland: Structure and Warning Signs",
    intro: "The udder is where your work turns into litres. Knowing its parts helps you spot trouble before it shows in the bucket.",
    image: {
      src: IMGS.mammary,
      alt: "Dairy goat udder anatomy with mammary gland, teats, and suspensory ligaments",
      caption: "A healthy udder is symmetrical, well-attached, with medium depth (not hanging below the hocks). Teats point slightly forward.",
      annotations: [
        { x: 45, y: 70, label: "Left mammary half" },
        { x: 65, y: 70, label: "Right mammary half" },
        { x: 38, y: 88, label: "Left teat" },
        { x: 62, y: 88, label: "Right teat" },
        { x: 52, y: 55, label: "Medial suspensory ligament" },
        { x: 30, y: 50, label: "Fore udder attachment" },
        { x: 72, y: 50, label: "Rear udder attachment" },
      ],
    },
    items: [
          {
                "h": "Two halves, working separately",
                "p": "A goat's udder has two halves, left and right, each with its own teat and milk-making tissue. Infection in one half does not automatically spread to the other, but you will see the udder go uneven before the milk looks wrong."
          },
          {
                "h": "What a good udder looks like",
                "p": "The Kenya Agricultural and Livestock Research Organization asks for a prominent, deep, well-attached udder with well-placed, long teats. A well-attached udder is held up firmly under the body. A hanging or loosely attached udder is harder to milk and easier to injure."
          },
          {
                "h": "Teats matter for milking",
                "p": "Well-placed, long teats are easier to milk by hand. Teats that are very short, point sideways, or hang close to the ground make milking hard and raise the chance of injury and infection."
          },
          {
                "h": "Preventing mastitis",
                "p": "Mastitis is infection of the udder. Washington State University lists the main ways to prevent it: sanitary conditions, good milking procedures, ventilated housing and dry bedding. Injuries to the udder and teat ends also let infection in."
          },
          {
                "h": "A hard udder with no milk",
                "p": "Caprine arthritis encephalitis can cause a hard, full udder that gives no milk, most often in does kidding for the first time (British Columbia Ministry of Agriculture). If a first-time mother has a hard udder and little milk, tell your veterinarian."
          },
          {
                "h": "Check yourself",
                "p": "Question: Name two ways to prevent mastitis. Answer: Any two of clean conditions, good milking procedures, ventilated housing, and dry bedding."
          }
    ],
    healthSignals: [
          {
                "sign": "Both halves are the same size and firmness",
                "meaning": "Normal udder. Check at every milking.",
                "flag": "green"
          },
          {
                "sign": "One half bigger or harder than the other",
                "meaning": "Early sign of trouble. Strip the milk from both halves and compare. Tell your veterinarian.",
                "flag": "amber"
          },
          {
                "sign": "Watery, clotted or discoloured milk",
                "meaning": "Possible mastitis. Separate the doe, keep the udder clean and call your veterinarian.",
                "flag": "red"
          },
          {
                "sign": "Hot, swollen, painful udder",
                "meaning": "Severe mastitis that can get worse quickly. Call your veterinarian at once.",
                "flag": "red"
          },
          {
                "sign": "Udder hangs very low",
                "meaning": "Weak attachment. More risk of injury and dirt. Note it when deciding whether to keep her for breeding.",
                "flag": "amber"
          },
          {
                "sign": "Hard udder with little or no milk in a first-time mother",
                "meaning": "Can be a sign of CAE. Ask your veterinarian about testing.",
                "flag": "red"
          }
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", "Washington State University Extension, Dairy Goat Production", "British Columbia Ministry of Agriculture, Caprine Arthritis Encephalitis"],
  },

  "musculoskeletal": {
    title: "Bones, Joints and Hooves",
    intro: "A goat that cannot walk well cannot graze, breed or be milked well. Lameness is easier to prevent than to cure.",
    image: {
      src: IMGS.musculoskeletal,
      alt: "Dairy goat hoof and leg anatomy with joint and hoof wall callouts",
      caption: "Check hooves at least every 2 months. Overgrown claws spiral inward and cause joint stress all the way up to the carpus and stifle.",
      annotations: [
        { x: 50, y: 60, label: "Carpus (knee joint)" },
        { x: 50, y: 70, label: "Pastern" },
        { x: 35, y: 85, label: "Medial claw" },
        { x: 65, y: 85, label: "Lateral claw" },
        { x: 50, y: 93, label: "Hoof wall & sole" },
      ],
    },
    items: [
          {
                "h": "Why legs and feet decide her working life",
                "p": "A dairy doe walks, grazes and stands to be milked every day. The Kenya Agricultural and Livestock Research Organization asks for strong legs and feet in breeding does and lists poor leg conformation as a reason to cull."
          },
          {
                "h": "Joints to know by name",
                "p": "Look at the knee (carpus), the pastern above the hoof, and the large joints of the hind leg. Swollen, warm or painful joints, often the knees, with lameness that keeps coming back, can be CAE arthritis (British Columbia Ministry of Agriculture)."
          },
          {
                "h": "The hoof",
                "p": "Each foot has two claws. Most lameness starts in the hoof: overgrown claws, stones or thorns, or infection between the claws. Trim hooves regularly. Ask your veterinary officer how often is right for your area and ground."
          },
          {
                "h": "Watch her walk",
                "p": "Watch every doe walk each day, in the same place. Uneven steps, resting one leg, or lagging behind the others are early signs. Goats hide pain, so a slight limp is already a message."
          },
          {
                "h": "What CAE support looks like",
                "p": "CAE has no specific treatment. Support means pain relief from your veterinarian, regular hoof trimming and deep bedding (British Columbia Ministry of Agriculture)."
          },
          {
                "h": "Check yourself",
                "p": "Question: A doe has swollen knees and keeps limping. Which disease should you ask your veterinarian about? Answer: Caprine arthritis encephalitis (CAE)."
          }
    ],
    healthSignals: [
          {
                "sign": "Walks evenly on all four legs",
                "meaning": "Normal. Keep checking daily.",
                "flag": "green"
          },
          {
                "sign": "Slight unevenness or resting one leg",
                "meaning": "Early lameness. Lift and look at all four hooves the same day.",
                "flag": "amber"
          },
          {
                "sign": "Clear limp or walks on three legs",
                "meaning": "Check for a stone, overgrowth or infection between the claws. Call your veterinarian if it does not improve in a day or two.",
                "flag": "red"
          },
          {
                "sign": "Swollen, warm or painful knees or other joints",
                "meaning": "Possible CAE or joint infection. Ask your veterinarian about testing.",
                "flag": "red"
          },
          {
                "sign": "Overgrown claws turning in or curling",
                "meaning": "Trim now. Overgrowth puts stress on the joints.",
                "flag": "amber"
          }
    ],
    sources: ["Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", "British Columbia Ministry of Agriculture, Caprine Arthritis Encephalitis"],
  },

  "integumentary": {
    title: "Skin, Coat, Eyes and Nose",
    intro: "Coat, eyes and nose show what is happening inside the goat, often before other signs appear.",
    image: {
      src: IMGS.integumentary,
      alt: "Dairy goat head showing eyes, mucous membranes, nasal discharge areas, and ear condition",
      caption: "The FAMACHA eye score is done on the lower eyelid conjunctiva — pull it down gently and compare to the score card. Aim for score 1–2.",
      annotations: [
        { x: 30, y: 35, label: "Eye — conjunctiva (FAMACHA)" },
        { x: 55, y: 55, label: "Nostril (nasal discharge)" },
        { x: 70, y: 30, label: "Ear (tag damage, pathology)" },
        { x: 45, y: 75, label: "Muzzle & lips" },
        { x: 20, y: 55, label: "Submandibular lymph node" },
      ],
    },
    items: [
          {
                "h": "The coat is a report card",
                "p": "A healthy goat has a smooth, glossy coat. A dull, rough or patchy coat can come from poor feed, worms or illness. It is one of the first things you see, so write it down when it changes."
          },
          {
                "h": "The FAMACHA eye check",
                "p": "FAMACHA is a colour card used to check for anaemia in goats and sheep. Anaemia is low blood. In grazing goats the most common cause is the barber pole worm. You match the colour of the lower eyelid lining to one of five scores (University of Rhode Island)."
          },
          {
                "h": "How to do it",
                "p": "Cover, push, pull, pop. Roll the upper eyelid down over the eyeball, press gently, pull the lower eyelid down, and score the exposed lining, not the inside of the eyelid. Do it in daylight, without shading the eye. Score both eyes and use the higher score."
          },
          {
                "h": "What the scores mean",
                "p": "Score 1 means not anaemic. Score 5 means severely anaemic. Scores 4 and 5 always need deworming. Scores 1 and 2 do not need deworming unless there are other signs, such as diarrhoea, poor body condition or a dull coat. Score 3 needs a decision: deworm if more than one in ten animals in the herd score 4 or 5, if the animal is a kid, if she is pregnant or milking, or if she is in poor condition or sick (University of Rhode Island)."
          },
          {
                "h": "Nose and breathing: think CCPP",
                "p": "Contagious caprine pleuropneumonia, called CCPP, is a serious lung disease of goats. Signs include weakness, lagging behind the flock, coughing, stringy saliva, nasal discharge, open-mouth breathing and seeking shade because of fever. Death rates are reported at 80 to 100 percent in infected goats (Kenya Agricultural and Livestock Research Organization). Isolate the goat and call your veterinary officer at once."
          },
          {
                "h": "Lumps under the skin",
                "p": "Caseous lymphadenitis, called CLA, causes abscesses in the skin and in the lymph nodes. Do not open an abscess near other animals. Separate the goat, call your veterinarian, and wear disposable gloves because the disease can infect people (eXtension)."
          },
          {
                "h": "Check yourself",
                "p": "Question: A doe scores 5 on FAMACHA. What do you do? Answer: Deworm her, and call your veterinarian because she is severely anaemic."
          }
    ],
    healthSignals: [
          {
                "sign": "Smooth, glossy coat",
                "meaning": "Normal. Keep checking.",
                "flag": "green"
          },
          {
                "sign": "Dull, rough or patchy coat",
                "meaning": "Possible poor feed, worms or illness. Check body condition and do a FAMACHA check.",
                "flag": "amber"
          },
          {
                "sign": "FAMACHA score 1 or 2",
                "meaning": "Do not deworm unless there are other signs such as diarrhoea, poor body condition or a dull coat.",
                "flag": "green"
          },
          {
                "sign": "FAMACHA score 3",
                "meaning": "Decide using the rules: deworm kids, pregnant or milking does, thin or sick animals, or all score 3 animals if more than one in ten score 4 or 5.",
                "flag": "amber"
          },
          {
                "sign": "FAMACHA score 4 or 5",
                "meaning": "Always deworm. Score 5 is severe anaemia: call your veterinarian.",
                "flag": "red"
          },
          {
                "sign": "Coughing, nasal discharge, open-mouth breathing, fever",
                "meaning": "Possible CCPP. Separate the goat and call your veterinary officer at once.",
                "flag": "red"
          },
          {
                "sign": "Firm lump under the skin, especially at a lymph node",
                "meaning": "Possible CLA. Do not lance it. Separate the goat and ask your veterinarian.",
                "flag": "red"
          }
    ],
    sources: ["University of Rhode Island Sheep and Goat Program, FAMACHA Scoring", "Kenya Agricultural and Livestock Research Organization, How to Identify and Control CCPP", "eXtension Goat Community of Practice, Caseous Lymphadenitis"],
  },

  "body-condition": {
    title: "Body Condition Scoring",
    intro: "Body condition scoring is free, needs no equipment and tells you more than most people expect.",
    image: {
      src: IMGS.bodyCondition,
      alt: "Dairy goat showing the loin, ribs, and sternum — the three BCS assessment points",
      caption: "Palpate (feel) the loin (behind the last rib), the ribs, and the sternum (breastbone). Do NOT rely on visual assessment alone — wool, coat, and pregnancy hide the truth.",
      annotations: [
        { x: 62, y: 45, label: "Loin (primary BCS point)" },
        { x: 45, y: 50, label: "Short ribs (lateral processes)" },
        { x: 38, y: 65, label: "Rib cage" },
        { x: 50, y: 78, label: "Sternum / brisket" },
      ],
    },
    items: [
          {
                "h": "Feel, do not just look",
                "p": "Body condition scoring is done with your hands. Hair, belly size and pregnancy can hide how thin or fat a goat really is. eXtension scores three areas: the loin (lumbar area), the breastbone (sternum) and the rib cage."
          },
          {
                "h": "The loin first",
                "p": "Find the bony points along the back, behind the last rib, with your fingertips. Grasp one between your thumb and forefinger. Use your whole hand to judge the muscle and fat over the loin. Slide your fingers under the side bones to feel how deep the cover is."
          },
          {
                "h": "The scale",
                "p": "The scale runs from 1.0 to 5.0 in half steps. A score of 1.0 is extremely thin with no fat reserve. Scores of 1.5 and 2.0 point to a feeding or health problem. Scores from 2.5 to 4.0 are healthy. Scores of 4.5 and 5.0 are very fat and rarely seen under normal care (eXtension)."
          },
          {
                "h": "Targets for a milking doe",
                "p": "Washington State University gives a body condition score of 3 as the aim when does enter lactation. High producers lose condition early in lactation and regain it later, so score her again after she has been milking for a few weeks."
          },
          {
                "h": "Change takes weeks",
                "p": "Condition does not change in a day. A thin doe will not recover in the week before kidding. Score every doe regularly, write down the number, and act early when it falls."
          },
          {
                "h": "Check yourself",
                "p": "Question: Which scores point to a feeding or health problem? Answer: 1.5 and 2.0 or lower."
          }
    ],
    healthSignals: [
          {
                "sign": "Score 2.5 to 4.0",
                "meaning": "Healthy range. Keep the feeding plan.",
                "flag": "green"
          },
          {
                "sign": "Score 3 as lactation starts",
                "meaning": "The aim for a doe about to start milking.",
                "flag": "green"
          },
          {
                "sign": "Score 1.5 or 2.0",
                "meaning": "A feeding or health problem. Check feed, worms and the FAMACHA eye score.",
                "flag": "red"
          },
          {
                "sign": "Score 1.0",
                "meaning": "Extremely thin. Call your veterinarian and fix feed at once.",
                "flag": "red"
          },
          {
                "sign": "Score drops by half a point or more between checks",
                "meaning": "Find out why: feed, worms, illness or heavy milking.",
                "flag": "amber"
          }
    ],
    sources: ["eXtension Goat Community of Practice, Goat Body Condition Score How-To", "Washington State University Extension, Dairy Goat Production"],
  },

  "lymph-nodes": {
    title: "Lymph Nodes, CLA and CAE",
    intro: "You cannot see inside a goat, but swollen lymph nodes and joints show you when something is wrong inside.",
    image: {
      src: IMGS.lymphNodes,
      alt: "Dairy goat body showing locations of major palpable lymph nodes",
      caption: "The five main palpable lymph node sites. Palpate them as part of every routine health check — 30 seconds, no equipment needed.",
      annotations: [
        { x: 25, y: 40, label: "Prescapular (shoulder)" },
        { x: 20, y: 55, label: "Submandibular (jaw)" },
        { x: 70, y: 45, label: "Prefemoral (flank)" },
        { x: 75, y: 70, label: "Popliteal (behind stifle)" },
        { x: 52, y: 78, label: "Supramammary (udder rear)" },
      ],
    },
    items: [
          {
                "h": "Five lymph node sites",
                "p": "Lymph nodes are small glands that swell when the body fights infection. The ones you can feel are under the jaw (submandibular), in front of the shoulder (prescapular), in the fold of the flank (prefemoral), behind the stifle (popliteal) and behind the udder (supramammary). Feel them gently as part of a routine check."
          },
          {
                "h": "Normal and not normal",
                "p": "A normal node is small, smooth and moves under your fingers. A node that is large, hot, hard or stuck to the tissue around it needs attention. Write down which side and how big."
          },
          {
                "h": "CLA: the slow herd problem",
                "p": "CLA is caused by a bacterium that lives for a long time in dark, damp places, soil and manure. It usually enters through wounds from dirty shears, needles or sharp objects. Abscesses that burst spread pus to feed, water and the ground (eXtension)."
          },
          {
                "h": "What to do about CLA",
                "p": "Isolate affected goats. Do not open an abscess near other animals. Surgical removal by a veterinarian is preferred. Keep housing free of sharp edges, and clean and disinfect equipment after each use. Culling affected animals is one control option. Vaccination can reduce abscesses but will not remove the disease (eXtension)."
          },
          {
                "h": "CAE: the viral disease",
                "p": "CAE is a viral disease with no cure and no vaccine. The main way it spreads is kids drinking infected colostrum or milk. Control means a closed herd or buying only from low-risk herds, blood testing animals before they come in, and not feeding kids pooled milk or colostrum (British Columbia Ministry of Agriculture)."
          },
          {
                "h": "Check yourself",
                "p": "Question: What is the main way CAE spreads to kids? Answer: Drinking infected colostrum or milk."
          }
    ],
    healthSignals: [
          {
                "sign": "Small, smooth lymph nodes that move under the fingers",
                "meaning": "Normal.",
                "flag": "green"
          },
          {
                "sign": "One node a little bigger but soft",
                "meaning": "Watch it weekly. Look for a nearby wound or skin problem.",
                "flag": "amber"
          },
          {
                "sign": "Firm, round lump at a lymph node site",
                "meaning": "Possible CLA. Do not open it. Separate the goat and ask your veterinarian.",
                "flag": "red"
          },
          {
                "sign": "Swollen joints with weight loss",
                "meaning": "Possible CAE. Ask your veterinarian about blood testing.",
                "flag": "red"
          },
          {
                "sign": "Nodes behind the udder enlarged",
                "meaning": "Possible udder infection or CLA. Have the milk and udder checked.",
                "flag": "red"
          }
    ],
    sources: ["eXtension Goat Community of Practice, Caseous Lymphadenitis", "British Columbia Ministry of Agriculture, Caprine Arthritis Encephalitis"],
  },

  "daily-check": {
    title: "Your Daily, Weekly and Monthly Checks",
    intro: "Good health is mostly routine. This lesson puts the earlier lessons into a simple plan.",
    items: [
          {
                "h": "The five-minute daily check",
                "p": "Do this at the same time every day, for example before milking. One: watch her walk. Two: look at her eyes, nose and coat. Three: look at the left flank and see if she is chewing her cud. Four: look at the udder and milk. Five: write down anything unusual."
          },
          {
                "h": "Weekly",
                "p": "Feel the lymph nodes. Do a FAMACHA eye check on goats that are thin, kids, and does that are pregnant or milking. Watch the herd as a whole for goats that lag behind."
          },
          {
                "h": "Monthly",
                "p": "Score each doe's body condition and write the number in her record. Look at hooves and trim when needed."
          },
          {
                "h": "Write it down",
                "p": "A record turns a feeling into a decision. Date, goat number, what you saw, what you did. MbuziPlus keeps each goat's record in one place so your veterinary officer and your field officer can see the history."
          },
          {
                "h": "When to call the veterinary officer",
                "p": "Call the same day for: a tight left flank, a goat that stops eating, hard or hot udder, coughing with a nasal discharge or fever, a FAMACHA score of 5, a kid that cannot stand, or any lump that opens. Isolate the goat first."
          },
          {
                "h": "Check yourself",
                "p": "Question: Name two things you do every day and two things you do every month. Answer: Daily: watch her walk, check eyes, nose, coat, left flank, cud and udder. Monthly: body condition score and hoof check."
          }
    ],
    sources: ["University of Rhode Island Sheep and Goat Program, FAMACHA Scoring", "eXtension Goat Community of Practice, Goat Body Condition Score How-To", "Kenya Agricultural and Livestock Research Organization, How to Identify and Control CCPP"],
  },

  "worked-example": {
    title: "Worked Example: Wanjiru Checks Her Does",
    intro: "This example uses simple, made-up numbers to show how the checks fit together. It is practice, not a promise of what will happen on your farm.",
    items: [
          {
                "h": "The morning",
                "p": "Wanjiru checks her two does before milking. One doe, Njeri, is slower to come to the feed and stands apart from the other doe."
          },
          {
                "h": "What she checks",
                "p": "Eyes: the lower lining is pale. FAMACHA score 4. Left flank: hollow. Coat: dull. Body condition score by touch: about 2. Milk last evening: lower than usual."
          },
          {
                "h": "What this means",
                "p": "A FAMACHA score of 4 always needs deworming. A hollow flank, dull coat and low score point to worms. Njeri is also milking, which makes her more at risk."
          },
          {
                "h": "What she does",
                "p": "She separates Njeri from the other doe, writes down every finding with today's date, and calls her veterinary officer to confirm the deworming product and dose. She does not guess the dose herself."
          },
          {
                "h": "What she checks next",
                "p": "She checks the other doe's eyes too. She rechecks Njeri's eyes and body condition in the days that follow, and writes the numbers in her record. She checks the pasture and feed so the cause is fixed."
          },
          {
                "h": "What she learns",
                "p": "Looking, touching and writing took ten minutes. Because she acted early, she is still earning from Njeri's milk instead of losing her."
          },
          {
                "h": "Check yourself",
                "p": "Question: Njeri scores 4 on FAMACHA. What does the rule say? Answer: Always deworm, with the product and dose confirmed by a veterinary officer."
          }
    ],
    sources: ["University of Rhode Island Sheep and Goat Program, FAMACHA Scoring", "eXtension Goat Community of Practice, Goat Body Condition Score How-To"],
  },

};

const LESSON_ORDER = [
  { id: "overview", type: "lesson", title: "The Dairy Goat at a Glance", duration: "20 min", icon: BookOpen },
  { id: "digestive", type: "lesson", title: "The Digestive System", duration: "25 min", icon: BookOpen },
  { id: "mammary", type: "lesson", title: "The Mammary Gland", duration: "25 min", icon: BookOpen },
  { id: "musculoskeletal", type: "lesson", title: "Bones, Joints and Hooves", duration: "20 min", icon: BookOpen },
  { id: "integumentary", type: "lesson", title: "Skin, Coat, Eyes and Nose", duration: "25 min", icon: BookOpen },
  { id: "body-condition", type: "lesson", title: "Body Condition Scoring", duration: "20 min", icon: BookOpen },
  { id: "lymph-nodes", type: "lesson", title: "Lymph Nodes, CLA and CAE", duration: "25 min", icon: BookOpen },
  { id: "daily-check", type: "lesson", title: "Your Daily, Weekly and Monthly Checks", duration: "20 min", icon: BookOpen },
  { id: "worked-example", type: "lesson", title: "Worked Example: Wanjiru Checks Her Does", duration: "20 min", icon: BookOpen },
  { id: "video", type: "video", title: "Watch: Dairy Goat Health Walkthrough", duration: "Watch anytime", icon: Film },
  { id: "quiz", type: "quiz", title: "Check Your Understanding", duration: "10 questions", icon: HelpCircle },
  { id: "journal", type: "journal", title: "Your Herd Health Inspection Plan", duration: "Write and save", icon: PenLine },
];

export const QUIZ = [
  { q: "You press your ear to a goat's left flank. Which statement is correct about the rumen?", options: ["The rumen is on the right side", "The rumen is on the left side, so you check the left flank for bloat or a hollow", "The rumen is under the udder", "You cannot check it from the outside"], correct: 1, why: "The rumen sits on the goat's left side. A tight left flank means bloat and a hollow one means she has not eaten enough." },
  { q: "Which stomach compartment is the true stomach with acid digestion?", options: ["Rumen", "Reticulum", "Omasum", "Abomasum"], correct: 3, why: "The abomasum is the true stomach, where hydrochloric acid digests food (Cornell University)." },
  { q: "A goat's left flank is drum-tight after grazing. What do you do?", options: ["Wait until tomorrow", "Treat it as an emergency and call your veterinarian at once", "Give her more feed", "Only milk her"], correct: 1, why: "A drum-tight left flank means bloat. Trapped gas can kill quickly, so call your veterinarian at once." },
  { q: "According to the Kenya Agricultural and Livestock Research Organization, which udder is desirable in a breeding doe?", options: ["A very small udder with short teats", "A prominent, deep, well-attached udder with well-placed, long teats", "Any udder if the goat is brown", "A hanging udder"], correct: 1, why: "The Kenya Agricultural and Livestock Research Organization asks for a prominent, deep, well-attached udder with well-placed, long teats." },
  { q: "Which are named ways to prevent mastitis by Washington State University?", options: ["Dirty bedding and crowded pens", "Sanitary conditions, good milking procedures, ventilated housing and dry bedding", "Milking less often", "Feeding more grain only"], correct: 1, why: "The listed measures are sanitary conditions, good milking procedures, ventilated housing and dry bedding." },
  { q: "A doe scores 4 on FAMACHA. What does the rule say?", options: ["Do nothing", "Always deworm", "Only deworm if she is a kid", "Wait a month"], correct: 1, why: "Scores 4 and 5 always need deworming (University of Rhode Island)." },
  { q: "A doe scores 3 on FAMACHA. When should you consider deworming her?", options: ["Never", "If she is a kid, pregnant or milking, thin or sick, or if more than one in ten of the herd score 4 or 5", "Only if she has long hair", "Only in the dry season"], correct: 1, why: "A score of 3 needs a decision based on the animal and the herd." },
  { q: "What body condition range is healthy for a goat?", options: ["1.0 to 1.5", "2.5 to 4.0", "4.5 to 5.0", "Any score"], correct: 1, why: "eXtension gives 2.5 to 4.0 as the healthy range. Scores of 1.5 and 2.0 point to a problem." },
  { q: "A goat has a cough, a nasal discharge, open-mouth breathing and fever. What do you do?", options: ["Treat with any antibiotic you have", "Separate her and call your veterinary officer at once, because it may be CCPP", "Mix her with the herd to share the disease", "Ignore it"], correct: 1, why: "CCPP spreads by droplets and has a very high death rate. Separate the goat and call a veterinary officer. The Kenya Agricultural and Livestock Research Organization does not recommend antibiotic treatment." },
  { q: "What is the main way CAE spreads to kids?", options: ["From the soil", "Kids drinking infected colostrum or milk", "From hay only", "From rain"], correct: 1, why: "The main route is kids drinking infected colostrum or milk. There is no cure and no vaccine." },
];

/* ─────────────────────────────────────────────
   FIRESTORE HELPERS
   Keyed by the account's unique access token — imfuyo_academy/{token}
   — NOT by phone number. Phone numbers can repeat, change format,
   or be missing; the token is the stable, unique doc ID used
   everywhere else in the app (onboarding gate, admin dashboard).
   ───────────────────────────────────────────── */
const TOPIC_ID = "anatomy";

function profileRef(u) {
  // u is the signed-in user; group members keep their own progress under pid.
  return doc(db, "imfuyo_academy", u.token, "mbuziplus_course", u.pid || u.token);
}

async function loadProgress(token) {
  try {
    const snap = await getDoc(profileRef(token));
    if (!snap.exists()) return {};
    return snap.data()[TOPIC_ID] || {};
  } catch (e) {
    console.error("loadProgress:", e);
    return {};
  }
}

async function saveProgress(token, patch) {
  try {
    await setDoc(
      profileRef(token),
      { [TOPIC_ID]: patch, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (e) {
    console.error("saveProgress:", e);
  }
}

/* ─────────────────────────────────────────────
   MOBILE HOOK
   ───────────────────────────────────────────── */
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return mobile;
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
   ───────────────────────────────────────────── */
export default function DairyGoatAnatomy({ isDark = false, user, onBack }) {
  const isMobile = useIsMobile();

  const [view,        setView]        = useState("home");
  const [loading,     setLoading]     = useState(true);
  const [completed,   setCompleted]   = useState({});
  const [answers,     setAnswers]     = useState({});
  const [revealed,    setRevealed]    = useState({});
  const [journal,     setJournal]     = useState("");
  const [journalSave, setJournalSave] = useState("idle");

  const saveTimer = useRef(null);
  const token      = user?.token ? user : null;

  /* ── colours ── */
  const bg       = isDark ? "#0d1a0f" : "#f5f9f5";
  const cardBg   = isDark ? "#13231a" : "#ffffff";
  const border   = isDark ? "#223328" : "#e2efe2";
  const textMain = isDark ? "#f0f7f0" : "#0d1a0f";
  const textSub  = isDark ? "#7fa183" : "#5a7a5d";
  const accent   = "#3E7A32";

  /* ── load ── */
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    loadProgress(token).then((saved) => {
      if (saved.completed)               setCompleted(saved.completed);
      if (saved.answers)                 setAnswers(saved.answers);
      if (saved.revealed)                setRevealed(saved.revealed);
      if (typeof saved.journal === "string") setJournal(saved.journal);
      setLoading(false);
    });
  }, [token]);

  const persist = useCallback((patch) => {
    if (!token) return;
    saveProgress(token, patch);
  }, [token]);

  function markDone(id) {
    setCompleted((prev) => {
      if (prev[id]) return prev;
      const next = { ...prev, [id]: true };
      persist({ completed: next });
      return next;
    });
  }

  function openLesson(id) {
    setView(id);
    markDone(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goNext() {
    const idx = LESSON_ORDER.findIndex((l) => l.id === view);
    if (idx >= 0 && idx < LESSON_ORDER.length - 1) {
      openLesson(LESSON_ORDER[idx + 1].id);
    } else {
      setView("home");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function choose(qi, oi) {
    if (revealed[qi]) return;
    const nextA = { ...answers,  [qi]: oi   };
    const nextR = { ...revealed, [qi]: true };
    setAnswers(nextA);
    setRevealed(nextR);
    persist({ answers: nextA, revealed: nextR });
  }

  function handleJournalChange(val) {
    setJournal(val);
    setJournalSave("idle");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setJournalSave("saving");
      try {
        await saveProgress(token, { journal: val });
        setJournalSave("saved");
      } catch {
        setJournalSave("error");
      }
    }, 900);
  }

  /* ── stats ── */
  const doneCount    = Object.keys(completed).length;
  const totalCount   = LESSON_ORDER.length;
  const progressPct  = Math.min(100, Math.round((doneCount / totalCount) * 100));
  const quizCorrect  = Object.entries(answers).filter(([qi, ai]) => QUIZ[+qi].correct === ai).length;
  const quizAnswered = Object.keys(revealed).length;

  /* ── LOADING ── */
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif" }}>
        <style>{css}</style>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <Loader2 size={28} color={accent} style={{ animation: "spin 1s linear infinite" }} />
          <p style={{ margin: 0, fontSize: "14px", color: textSub, fontWeight: 600 }}>Loading your progress…</p>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     SHARED CHROME
     ───────────────────────────────────────── */
  const Header = ({ title, onBackClick }) => (
    <div className="da-fade" style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "20px 18px 16px" : "28px 32px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
      <button
        onClick={onBackClick}
        style={{
          display: "inline-flex", alignItems: "center", gap: "6px",
          background: "none", border: `1px solid ${isDark ? "#243529" : "#c8e0c8"}`,
          borderRadius: "50px", padding: "7px 16px",
          fontSize: "12px", fontWeight: 600, color: textSub,
          cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = accent; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? "#243529" : "#c8e0c8"; e.currentTarget.style.color = textSub; }}
      >
        <ChevronLeft size={13} /> {view === "home" ? "Course" : "Anatomy"}
      </button>
      {title && (
        <h2 style={{ margin: 0, fontSize: isMobile ? "14px" : "16px", fontWeight: 700, color: textMain, fontFamily: "Georgia, 'Times New Roman', serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </h2>
      )}
    </div>
  );

  const NextButton = ({ label = "Continue" }) => (
    <button
      onClick={goNext}
      style={{
        display: "inline-flex", alignItems: "center", gap: "8px",
        background: accent, color: "#fff", border: "none",
        borderRadius: "14px", padding: "13px 26px",
        fontSize: "14px", fontWeight: 800, cursor: "pointer",
        fontFamily: "inherit", letterSpacing: "-0.01em",
        boxShadow: "0 4px 20px rgba(34,197,94,0.4)",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#285020"}
      onMouseLeave={e => e.currentTarget.style.background = accent}
    >
      {label} <ChevronRight size={15} />
    </button>
  );

  /* ─────────────────────────────────────────
     HOME
     ───────────────────────────────────────── */
  if (view === "home") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title={null} onBackClick={onBack} />

        <main style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "0 18px 80px" : "0 32px 80px" }}>

          <ModuleIntro moduleId="goat-anatomy" isDark={isDark} onStart={() => document.getElementById("mbz-lessons")?.scrollIntoView({ behavior: "smooth" })} />

          {/* Hero */}
          <div className="da-fade" style={{ marginBottom: "32px" }}>
            <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: isDark ? "#3a7040" : "#5a9060" }}>
              Module 5 of 42 · Dairy Goat Anatomy & Health
            </span>
            <h1 style={{
              margin: "8px 0 8px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "26px" : "34px",
              fontWeight: 700, color: textMain, lineHeight: 1.15, letterSpacing: "-0.02em",
            }}>
              Know the body. Read the signs.<br />Catch problems before they cost you.
            </h1>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: textSub, lineHeight: 1.65, maxWidth: "540px" }}>
              Every lesson includes annotated images showing exactly where to look on a real goat — and a quick-reference panel of what healthy, monitored, and urgent signs look like.
            </p>

            {/* Progress */}
            <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "160px", height: "5px", borderRadius: "3px", background: isDark ? "#1e3321" : "#dbeedd", overflow: "hidden" }}>
                  <div style={{ width: `${progressPct}%`, height: "100%", background: accent, borderRadius: "3px", transition: "width 0.5s ease" }} />
                </div>
                <span style={{ fontSize: "12px", color: textSub, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {doneCount}/{totalCount} opened
                </span>
              </div>
              {quizAnswered > 0 && (
                <span style={{
                  fontSize: "12px", fontWeight: 700,
                  color: isDark ? "#8DBB7E" : "#285020",
                  background: isDark ? "#14301A" : "#E3EEDD",
                  padding: "3px 12px", borderRadius: "50px",
                  border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
                }}>
                  Quiz: {quizCorrect}/{quizAnswered} correct
                </span>
              )}
            </div>
          </div>

          <span id="mbz-lessons" />
          {/* Lesson list */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {LESSON_ORDER.map((l, i) => {
              const Icon   = l.icon;
              const isDone = !!completed[l.id];

              let chip = null;
              if (l.id === "quiz" && quizAnswered > 0) {
                chip = (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#8DBB7E" : "#285020", background: isDark ? "#14301A" : "#E3EEDD", padding: "2px 10px", borderRadius: "50px", border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}`, whiteSpace: "nowrap" }}>
                    {quizCorrect}/{quizAnswered}
                  </span>
                );
              }
              if (l.id === "journal" && journal.trim().length > 0) {
                chip = (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#93c5fd" : "#1d4ed8", background: isDark ? "#0c1a2e" : "#eff6ff", padding: "2px 10px", borderRadius: "50px", border: `1px solid ${isDark ? "#1e3a5f" : "#bfdbfe"}`, whiteSpace: "nowrap" }}>
                    {journal.trim().split(/\s+/).filter(Boolean).length}w saved
                  </span>
                );
              }

              return (
                <div
                  key={l.id}
                  className="da-card da-fade"
                  onClick={() => openLesson(l.id)}
                  style={{
                    animationDelay: `${i * 0.035}s`,
                    display: "flex", alignItems: "center", gap: "14px",
                    background: cardBg,
                    border: `1px solid ${isDone ? (isDark ? "#14301A" : "#B7D9A8") : border}`,
                    borderRadius: "14px", padding: "14px 16px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = accent}
                  onMouseLeave={e => e.currentTarget.style.borderColor = isDone ? (isDark ? "#14301A" : "#B7D9A8") : border}
                >
                  <div style={{
                    flexShrink: 0, width: "38px", height: "38px", borderRadius: "10px",
                    background: isDone ? (isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)") : (isDark ? "#0f1f13" : "#e8f5e8"),
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={17} color={isDone ? accent : (isDark ? "#3a7040" : "#9abf9a")} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: "0 0 2px", fontSize: "14px", fontWeight: 700, color: isDone ? (isDark ? "#B7D9A8" : "#14301A") : textMain }}>
                      {l.title}
                    </p>
                    <p style={{ margin: 0, fontSize: "11.5px", color: textSub }}>{l.duration}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                    {chip}
                    {isDone
                      ? <CheckCircle2 size={19} color={accent} />
                      : <Circle size={19} color={isDark ? "#2a3d2e" : "#cfe5cf"} />
                    }
                  </div>
                </div>
              );
            })}
          </div>
          <SourcesPanel moduleId="goat-anatomy" isDark={isDark} />
        </main>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     LESSON VIEW
     ───────────────────────────────────────── */
  if (LESSONS[view]) {
    const lesson = LESSONS[view];

    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title={lesson.title} onBackClick={() => setView("home")} />

        <main data-mbz-lesson="1" style={{ maxWidth: "720px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          {/* Title */}
          <div className="da-fade" style={{ marginBottom: "24px" }}>
            <h1 style={{
              margin: "0 0 12px",
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: isMobile ? "24px" : "30px",
              fontWeight: 700, color: textMain, lineHeight: 1.2, letterSpacing: "-0.02em",
            }}>
              {lesson.title}
            </h1>
            <p style={{
              margin: 0, fontSize: isMobile ? "15px" : "16px", lineHeight: 1.75,
              color: isDark ? "#9fd4a8" : "#3a6b40",
              fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic",
              paddingLeft: "16px", borderLeft: `3px solid ${isDark ? "#14301A" : "#B7D9A8"}`,
            }}>
              {lesson.intro}
            </p>
          </div>

          {/* Annotated image — its own image per section, sized to fit naturally */}
          {lesson.image && (
            <div className="da-fade" style={{ animationDelay: "0.1s" }}>
              <AnnotatedImage
                src={lesson.image.src}
                alt={lesson.image.alt}
                annotations={lesson.image.annotations || []}
                caption={lesson.image.caption}
                isDark={isDark}
              />
            </div>
          )}

          {/* Divider */}
          <div style={{ height: "1px", background: isDark ? "#1e3321" : "#d8edd8", margin: "0 0 28px" }} />

          {/* Field-notebook insights */}
          <div className="da-fade" style={{ paddingLeft: "4px" }}>
            {lesson.items.map((item, i) => (
              <InsightBlock
                key={i}
                item={item}
                isDark={isDark}
                index={i === lesson.items.length - 1 ? -1 : i}
              />
            ))}
          </div>

          {/* Health signals panel */}
          {lesson.healthSignals && (
            <div className="da-fade" style={{ animationDelay: "0.2s" }}>
              <HealthSignalPanel signals={lesson.healthSignals} isDark={isDark} />
            </div>
          )}

          {lesson.sources && (
            <div style={{ marginTop: "28px", padding: "14px 16px", borderRadius: "12px", background: isDark ? "#0f2014" : "#eef6ea", fontSize: "12.5px", lineHeight: 1.6, color: isDark ? "#9fb7a2" : "#4b6a50" }}>
              <b>Sources:</b> {lesson.sources.join("; ")}.
            </div>
          )}

          <div style={{ marginTop: "36px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     VIDEO
     ───────────────────────────────────────── */
  if (view === "video") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Health Walkthrough Video" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "860px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>
          <h1 className="da-fade" style={{ margin: "0 0 8px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: isMobile ? "24px" : "30px", fontWeight: 700, color: textMain, lineHeight: 1.2 }}>
            Dairy Goat Health — Video Walkthrough
          </h1>
          <p className="da-fade" style={{ margin: "0 0 20px", fontSize: "14px", color: textSub, lineHeight: 1.7, maxWidth: "560px" }}>
            A practical farm walkthrough covering the body systems covered in this topic. Return anytime — it's a good reference after you've read the lessons once.
          </p>
          <div className="da-fade" style={{ borderRadius: "16px", overflow: "hidden", border: `1px solid ${border}`, background: "#000" }}>
            <video controls preload="metadata" poster={IMGS.videoPoster}
              style={{ width: "100%", display: "block", maxHeight: "480px", background: "#000" }}>
              <source src={IMGS.video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     QUIZ
     ───────────────────────────────────────── */
  if (view === "quiz") {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Check Your Understanding" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "740px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          <div className="da-fade" style={{ marginBottom: "24px" }}>
            <h1 style={{ margin: "0 0 8px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: isMobile ? "24px" : "30px", fontWeight: 700, color: textMain, lineHeight: 1.2 }}>
              Check Your Understanding
            </h1>
            <p style={{ margin: "0 0 12px", fontSize: "14px", color: textSub, lineHeight: 1.7 }}>
              Tap an answer to see whether it's correct and why. Your answers are saved automatically.
            </p>
            {quizAnswered > 0 && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: isDark ? "#8DBB7E" : "#285020", background: isDark ? "#14301A" : "#E3EEDD", padding: "4px 14px", borderRadius: "50px", border: `1px solid ${isDark ? "#14301A" : "#B7D9A8"}` }}>
                  {quizCorrect} / {quizAnswered} correct
                </span>
                <span style={{ fontSize: "12px", color: textSub }}>
                  {quizAnswered === QUIZ.length ? "All answered ✓" : `${QUIZ.length - quizAnswered} left`}
                </span>
              </div>
            )}
          </div>

          {QUIZ.map((item, qi) => {
            const chosen     = answers[qi];
            const isRevealed = !!revealed[qi];
            const isCorrect  = chosen === item.correct;

            return (
              <div key={qi} className="da-fade" style={{
                animationDelay: `${qi * 0.025}s`,
                background: cardBg,
                border: `1px solid ${isRevealed ? (isCorrect ? (isDark ? "#14301A" : "#B7D9A8") : (isDark ? "#7f1d1d" : "#fecaca")) : border}`,
                borderRadius: "16px", padding: "20px 22px", marginBottom: "12px",
              }}>
                <p style={{ margin: "0 0 14px", fontSize: "14.5px", fontWeight: 700, color: textMain, lineHeight: 1.55 }}>
                  <span style={{ color: isDark ? "#8DBB7E" : "#285020", marginRight: "6px", fontFamily: "Georgia, serif" }}>{qi + 1}.</span>
                  {item.q}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {optionOrder(`anatomy:${qi}`, item.options.length).map((oi) => {
                    const opt = item.options[oi];
                    let optBorder = isDark ? "#2a3d2e" : "#e2efe2";
                    let optBg     = "transparent";
                    let textCol   = isDark ? "#c8e0cc" : "#2d3e31";
                    let icon      = null;

                    if (isRevealed) {
                      if (oi === item.correct) {
                        optBorder = accent; optBg = isDark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)";
                        textCol = isDark ? "#B7D9A8" : "#14301A";
                        icon = <Check size={14} color={accent} style={{ flexShrink: 0 }} />;
                      } else if (oi === chosen) {
                        optBorder = "#ef4444"; optBg = isDark ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.06)";
                        textCol = isDark ? "#fca5a5" : "#dc2626";
                        icon = <X size={14} color="#ef4444" style={{ flexShrink: 0 }} />;
                      }
                    }

                    return (
                      <div key={oi} className="da-option" onClick={() => choose(qi, oi)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", border: `1.5px solid ${optBorder}`, background: optBg, borderRadius: "10px", padding: "11px 14px", fontSize: "13.5px", color: textCol, cursor: isRevealed ? "default" : "pointer", lineHeight: 1.5, userSelect: "none" }}>
                        <span>{opt}</span>
                        {icon}
                      </div>
                    );
                  })}
                </div>

                {isRevealed && (
                  <div className="da-fade" style={{ marginTop: "12px", padding: "12px 16px", borderRadius: "10px", background: isDark ? "#0f1f13" : "#F1F5EE", border: `1px solid ${isDark ? "#1e3321" : "#D5E8CB"}`, display: "flex", gap: "10px", alignItems: "flex-start" }}>
                    <div style={{ flexShrink: 0, width: "20px", height: "20px", borderRadius: "50%", background: isCorrect ? (isDark ? "#14301A" : "#E3EEDD") : (isDark ? "#7f1d1d" : "#fee2e2"), display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1px" }}>
                      {isCorrect ? <Check size={11} color={isDark ? "#8DBB7E" : "#285020"} /> : <X size={11} color={isDark ? "#fca5a5" : "#dc2626"} />}
                    </div>
                    <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.65, color: isDark ? "#9fd4a8" : "#3a6b40" }}>
                      <strong style={{ color: textMain, fontWeight: 700 }}>Why: </strong>{item.why}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton />
          </div>
        </main>
      </div>
    );
  }

  /* ─────────────────────────────────────────
     JOURNAL
     ───────────────────────────────────────── */
  if (view === "journal") {
    const statusLabel = { idle: "Not yet saved", saving: "Saving…", saved: "Saved", error: "Save failed — retry" }[journalSave];
    const statusColor = journalSave === "saved" ? accent : journalSave === "error" ? "#ef4444" : textSub;
    const wordCount   = journal.trim().split(/\s+/).filter(Boolean).length;

    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "'Inter','DM Sans',system-ui,sans-serif", paddingTop: "80px" }}>
        <style>{css}</style>
        <Header title="Your Herd Health Plan" onBackClick={() => setView("home")} />
        <main style={{ maxWidth: "680px", margin: "0 auto", padding: isMobile ? "0 18px 90px" : "0 32px 90px" }}>

          <h1 className="da-fade" style={{ margin: "0 0 8px", fontFamily: "Georgia, 'Times New Roman', serif", fontSize: isMobile ? "24px" : "30px", fontWeight: 700, color: textMain, lineHeight: 1.2 }}>
            Write Your Herd Health Inspection Plan
          </h1>
          <p className="da-fade" style={{ margin: "0 0 8px", fontSize: "14px", color: textSub, lineHeight: 1.7, maxWidth: "540px" }}>
            Write a short inspection routine for your own herd. Cover:
          </p>

          <ul className="da-fade" style={{ margin: "0 0 24px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "6px" }}>
            {[
              "Which body parts you'll check at each daily milking",
              "How often you'll do a full head-to-tail check (eyes, nodes, hooves, udder, BCS)",
              "Which health signal you're most worried about — and why",
              "One management gap on your farm you want to close after this topic",
            ].map((pt, i) => (
              <li key={i} style={{ display: "flex", gap: "10px", fontSize: "13.5px", color: isDark ? "#7fa183" : "#5a7a5d", lineHeight: 1.55 }}>
                <span style={{ color: accent, flexShrink: 0, fontWeight: 800 }}>→</span>{pt}
              </li>
            ))}
          </ul>

          <div className="da-fade" style={{
            background: cardBg,
            border: `1.5px solid ${journalSave === "error" ? "#ef4444" : isDark ? "#223328" : "#c8e0c8"}`,
            borderRadius: "14px", padding: "4px", transition: "border-color 0.15s",
          }}>
            <textarea
              value={journal}
              onChange={e => handleJournalChange(e.target.value)}
              placeholder="Start writing… e.g. 'Every morning before milking I'll check the FAMACHA score on any goat that looked dull the day before…'"
              style={{
                width: "100%", minHeight: "240px", resize: "vertical",
                border: "none", outline: "none", background: "transparent",
                color: textMain, fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: "15px", lineHeight: 1.75,
                padding: "14px 16px", boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "10px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: statusColor, fontWeight: 600 }}>
              {journalSave === "saving"
                ? <Loader2 size={12} color={textSub} style={{ animation: "spin 1s linear infinite" }} />
                : <Save size={12} />}
              {statusLabel}
            </span>
            <span style={{ fontSize: "12px", color: textSub }}>{wordCount} {wordCount === 1 ? "word" : "words"}</span>
          </div>

          <div style={{ marginTop: "20px", padding: "14px 18px", borderRadius: "12px", background: isDark ? "#0f1f13" : "#F1F5EE", border: `1px solid ${isDark ? "#1e3321" : "#D5E8CB"}` }}>
            <p style={{ margin: 0, fontSize: "12.5px", color: textSub, lineHeight: 1.65 }}>
              Saved to your account — accessible from any device. Come back and update it as your routine evolves.
            </p>
          </div>

          <div style={{ marginTop: "28px", display: "flex", justifyContent: "flex-end" }}>
            <NextButton label="Back to course" />
          </div>
        </main>
      </div>
    );
  }

  return null;
}